import { safe, state, threshold } from './config'
import { isActive } from './journey'
import { cleanStr, maskText, scrubPath } from './scrub'
import { record, reserve, snapshot } from './transport'

const INTERACTIVE = [
  'a',
  'button',
  'summary',
  'label',
  'input[type=checkbox]',
  'input[type=radio]',
  'input[type=submit]',
  'input[type=button]',
  'select',
  '[role=button]',
  '[role=link]',
  '[role=tab]',
  '[role=menuitem]',
  '[role=option]',
  '[role=checkbox]',
  '[role=radio]',
  '[role=switch]',
  'tr.cursor-pointer',
].join(',')
const TEXT_LABEL = 'button,a,summary,label,[role=button],[role=tab],[role=menuitem],[role=link],[role=switch]'
// Text in these comes from records (names, answers, file names): only data-helios-label or the icon is used there.
// V1 page titles are breadcrumb trails of record names ("<h1><a>Jane Doe</a> / Contract</h1>").
const DATA_CONTEXT = [
  'td',
  'tr',
  '[role=row]',
  '[role=option]',
  '[contenteditable]',
  '[data-slot=breadcrumb]',
  'nav[aria-label=breadcrumb i]',
  'h1',
  '[data-helios-mask]',
].join(',')
const USER_CHIP = '[data-slot=avatar]'
// These show the selected value as their text, so only their own name or field label is used.
const VALUE_TRIGGER = [
  '[role=combobox]',
  '[aria-haspopup=listbox]',
  '[data-slot=popover-trigger]',
  '[data-slot=select-trigger]',
  '[data-slot=combobox-trigger]',
].join(',')
const REGIONS = [
  '[role=dialog]',
  '[role=alertdialog]',
  '[data-slot=sheet-content]',
  'nav',
  'header',
  'aside',
  '[data-slot=sidebar]',
  '[role=toolbar]',
  'table',
  '[role=menu]',
  'form',
  '[data-sonner-toast]',
].join(',')
const NATIVE_CONTROL = 'input[type=checkbox],input[type=radio],select'
// Header, nav and toolbar controls change content elsewhere (the sidebar, a panel), so the whole page is watched.
const WIDE_REGIONS = new Set(['header', 'nav', 'toolbar'])
const DISABLED_UNDER_POINTER = 'button:disabled,[data-disabled][role]'
export const DIALOGS = '[role=dialog],[role=alertdialog]'

const ROLE_KINDS = {
  button: 'button',
  link: 'link',
  tab: 'tab',
  menuitem: 'menuitem',
  option: 'option',
  checkbox: 'checkbox',
  radio: 'radio',
  switch: 'switch',
}
const TAG_KINDS = { A: 'link', BUTTON: 'button', SUMMARY: 'summary', SELECT: 'select', TR: 'row' }
const MUTATION_ATTRIBUTES = ['class', 'aria-expanded', 'aria-checked', 'aria-selected', 'data-state', 'hidden', 'open']
const MODIFIER_KEYS = new Set([
  'Meta',
  'Control',
  'Shift',
  'Alt',
  'AltGraph',
  'CapsLock',
  'Fn',
  'OS',
  'Dead',
  'Process',
])
const EDITING_KEYS = new Set(['A', 'C', 'V', 'X', 'Y', 'Z'])
const SHORTCUT_CHAR = /^[A-Z0-9,./;'`=[\]\\-]$/
const NON_TEXT_INPUT_TYPES = new Set([
  'button',
  'checkbox',
  'radio',
  'submit',
  'reset',
  'file',
  'image',
  'color',
  'range',
])

let pending = []
let burst = null
let forwardedTo = null

export function elementOf(target) {
  if (target instanceof Element) {
    return target
  }

  return target?.parentElement ?? null
}

export function elementKind(el) {
  const role = el.getAttribute('role')

  if (role && ROLE_KINDS[role]) {
    return ROLE_KINDS[role]
  }

  if (el.tagName === 'INPUT') {
    if (el.type === 'checkbox' || el.type === 'radio') {
      return el.type
    }

    return el.type === 'submit' || el.type === 'button' ? 'button' : 'other'
  }

  return TAG_KINDS[el.tagName] ?? 'other'
}

function ownText(node) {
  if (!node || node.closest('[data-helios-mask]')) {
    return null
  }

  return node.getAttribute('data-helios-label') || node.textContent
}

function labelledByText(el) {
  const ids = el.getAttribute('aria-labelledby')

  if (!ids) {
    return null
  }

  return ids
    .split(/\s+/)
    .map((id) => ownText(document.getElementById(id)) ?? '')
    .join(' ')
    .trim()
}

function visibleText(el) {
  return el.matches(TEXT_LABEL) ? el.innerText : null
}

function iconName(el) {
  const svg = el.querySelector('svg[class*="lucide-"]')
  const name = svg ? Array.from(svg.classList).find((className) => className.startsWith('lucide-')) : null

  return name ? `[icon:${name.slice(7)}]` : null
}

function fixedLabel(el) {
  return el && !el.closest('[data-helios-mask]') ? el.getAttribute('data-helios-label') || null : null
}

// 'attr' when the target is the element's own data-helios-label, which Helios keeps as written.
export function labelSource(el, kind = el ? elementKind(el) : 'other') {
  return kind !== 'option' && kind !== 'row' && fixedLabel(el) ? 'attr' : undefined
}

export function resolveLabel(el, kind = el ? elementKind(el) : 'other') {
  if (!el || kind === 'option' || el.closest('[data-helios-mask]')) {
    return null
  }

  if (kind === 'row') {
    return 'row'
  }

  const fixed = fixedLabel(el)

  if (fixed || el.closest(DATA_CONTEXT) || el.querySelector(USER_CHIP)) {
    return maskText(fixed || iconName(el), 80)
  }

  if (el.matches(VALUE_TRIGGER)) {
    return maskText(
      el.getAttribute('aria-label') || el.getAttribute('title') || ownText(el.labels?.[0]) || iconName(el),
      80
    )
  }

  return maskText(
    el.getAttribute('aria-label') || labelledByText(el) || el.getAttribute('title') || visibleText(el) || iconName(el),
    80
  )
}

export function dialogTitle(dialog) {
  if (!dialog || dialog.closest('[data-helios-mask]')) {
    return null
  }

  return maskText(
    fixedLabel(dialog) ||
      labelledByText(dialog) ||
      dialog.getAttribute('aria-label') ||
      ownText(dialog.querySelector('h1,h2,h3,[data-slot$="title"]')),
    80
  )
}

export function regionName(el) {
  if (!el) {
    return 'main'
  }

  const role = el.getAttribute('role')
  const slot = el.getAttribute('data-slot')

  if (slot === 'sheet-content') {
    return 'sheet'
  }

  if (role === 'dialog' || role === 'alertdialog') {
    return 'dialog'
  }

  if (slot === 'sidebar' || el.tagName === 'ASIDE') {
    return 'sidebar'
  }

  if (role === 'toolbar' || role === 'menu') {
    return role
  }

  if (el.hasAttribute('data-sonner-toast')) {
    return 'toast'
  }

  return { NAV: 'nav', HEADER: 'header', TABLE: 'table', FORM: 'form' }[el.tagName] ?? 'main'
}

function columnHeader(node) {
  const cell = node.closest('td,th')
  const table = cell?.closest('table')

  if (!table) {
    return null
  }

  const header = table.tHead?.rows?.[0]?.cells?.[cell.cellIndex]

  return header && header !== cell ? maskText(header.textContent, 40) : null
}

function linkInfo(link) {
  const href = link?.getAttribute('href')

  if (!href || href.startsWith('#') || /^javascript:/i.test(href)) {
    return null
  }

  let url

  try {
    url = new URL(href, window.location.href)
  } catch {
    return null
  }

  if (url.protocol !== 'http:' && url.protocol !== 'https:') {
    return { external: true }
  }

  if (url.origin !== window.location.origin) {
    return { external: true, host: cleanStr(url.host, 64) }
  }

  return { path: scrubPath(url.pathname + url.search) }
}

function isDisabled(el) {
  return (
    el.disabled === true ||
    el.getAttribute('aria-disabled') === 'true' ||
    el.hasAttribute('data-disabled') ||
    el.closest('fieldset:disabled') !== null
  )
}

// shadcn buttons get pointer-events-none when disabled, so the click lands on a wrapper: look for one under the pointer.
function disabledUnder(node, x, y) {
  if (node === document.body || node === document.documentElement || node.tagName === 'MAIN') {
    return null
  }

  const candidates = node.querySelectorAll(DISABLED_UNDER_POINTER)

  for (let index = 0; index < candidates.length && index < 20; index += 1) {
    const rect = candidates[index].getBoundingClientRect()

    if (x >= rect.left && x <= rect.right && y >= rect.top && y <= rect.bottom) {
      return candidates[index]
    }
  }

  return null
}

function modifierOf(event) {
  if (event.metaKey) {
    return 'meta'
  }

  if (event.ctrlKey) {
    return 'ctrl'
  }

  if (event.shiftKey) {
    return 'shift'
  }

  return event.altKey ? 'alt' : undefined
}

// A double/triple click or drag that leaves text selected inside the element is a selection, not a press.
function selectionWithin(node) {
  try {
    const selection = window.getSelection?.()

    if (!selection || selection.isCollapsed) {
      return false
    }

    return (
      (node.contains(selection.anchorNode) || node.contains(selection.focusNode)) && selection.toString().trim() !== ''
    )
  } catch {
    return false
  }
}

function finalize(watch, effect) {
  const index = pending.indexOf(watch)

  if (index === -1) {
    return
  }

  pending.splice(index, 1)
  window.clearTimeout(watch.timer)
  watch.observer?.disconnect()

  let resolved = effect ?? 'none'

  // Native checkboxes, radios and selects change state without a DOM mutation; they are never dead clicks.
  if (resolved === 'none' && watch.native) {
    resolved = 'dom'
  }

  record('ui.click', { ...watch.fields, props: { ...watch.fields.props, effect: resolved } }, watch.slot)
}

function watchEffect(watch, regionEl) {
  pending.push(watch)
  watch.timer = window.setTimeout(
    safe(() => finalize(watch, null)),
    threshold('dead_click_ms')
  )

  try {
    const props = watch.fields.props
    const root =
      WIDE_REGIONS.has(props.region) || props.slot === 'sidebar-trigger'
        ? document.body
        : (regionEl ?? document.querySelector('main') ?? document.body)

    watch.observer = new MutationObserver(safe(() => finalize(watch, 'dom')))
    watch.observer.observe(root, {
      subtree: true,
      childList: true,
      attributes: true,
      attributeFilter: MUTATION_ATTRIBUTES,
    })
  } catch {
    watch.observer = null
  }
}

// The first effect after a click wins: navigation, request, dialog, toast or focus.
export function notifyEffect(effect) {
  const watch = pending[pending.length - 1]

  if (watch) {
    finalize(watch, effect)
  }
}

function burstDetails(el) {
  const kind = el.matches(INTERACTIVE) ? elementKind(el) : 'other'

  const masked = burst.snap.masked

  return {
    kind,
    label: masked ? null : resolveLabel(el, kind),
    labelSrc: masked ? undefined : labelSource(el, kind),
    region: regionName(el.closest(REGIONS)),
  }
}

function finishBurst() {
  const done = burst

  burst = null

  if (!done) {
    return
  }

  window.clearTimeout(done.timer)

  if (!done.details || done.selecting) {
    return
  }

  record(
    'ui.rage_click',
    {
      target: done.details.label,
      duration_ms: done.last - done.first,
      props: {
        count: done.count,
        el: done.details.kind,
        region: done.details.region,
        label_src: done.details.labelSrc,
      },
    },
    reserve(done.snap)
  )
}

function trackRage(event, el, selecting) {
  // Keyboard activations (detail 0) have no position and are not repeated presses.
  if (event.detail === 0) {
    return
  }

  const now = Date.now()
  const windowMs = threshold('rage_window_ms')
  const near =
    burst &&
    now - burst.last <= windowMs &&
    (burst.el === el || Math.hypot(event.clientX - burst.x, event.clientY - burst.y) <= threshold('rage_radius_px'))

  if (near) {
    burst.last = now
    burst.count += 1
    burst.recent = burst.recent.filter((time) => now - time <= windowMs).concat(now)
  } else {
    finishBurst()
    burst = {
      el,
      x: event.clientX,
      y: event.clientY,
      first: now,
      last: now,
      count: 1,
      recent: [now],
      snap: snapshot(),
      details: null,
      selecting: false,
      timer: null,
    }
  }

  burst.selecting = burst.selecting || selecting

  if (!burst.details && burst.recent.length >= threshold('rage_clicks')) {
    burst.details = burstDetails(el)
  }

  window.clearTimeout(burst.timer)
  burst.timer = window.setTimeout(safe(finishBurst), windowMs)
}

function onClick(event) {
  if (!isActive() || !event.isTrusted) {
    return
  }

  const raw = elementOf(event.target)

  if (!raw) {
    return
  }

  let el = raw.closest(INTERACTIVE)
  let disabled = false

  if (!el) {
    el = disabledUnder(raw, event.clientX, event.clientY)
    disabled = el !== null
  }

  const selecting = selectionWithin(el ?? raw)
  // Clicks in a text field place the caret or select its text (a triple click selects all), so they are never presses.
  const inTextField = isTextField(raw)

  // A label forwards the click to its control: the label's click counts as the press, the control's is recorded.
  if (el?.tagName === 'LABEL' && el.control) {
    if (!inTextField && el.control.matches(INTERACTIVE)) {
      trackRage(event, el.control, selecting)
    }

    forwardedTo = el.control
    window.setTimeout(() => {
      forwardedTo = null
    }, 0)

    return
  }

  if (!inTextField && (!el || el !== forwardedTo)) {
    trackRage(event, el ?? raw, selecting)
  }

  forwardedTo = null

  if (!el || selecting) {
    return
  }

  const slot = reserve()

  if (!slot) {
    return
  }

  const kind = elementKind(el)
  const masked = slot.ctx.masked
  const label = masked ? null : resolveLabel(el, kind)
  const regionEl = el.closest(REGIONS)
  const link = el.closest('a[href]')
  const href = linkInfo(link)
  const modifier = modifierOf(event)

  state.stats.clicks += 1
  state.lastTrigger = { seq: slot.seq, label, at: Date.now(), form: el.form ?? el.closest('form') }

  const watch = {
    slot,
    native: el.matches(NATIVE_CONTROL),
    fields: {
      target: label,
      props: {
        el: kind,
        label_src: masked ? undefined : labelSource(el, kind),
        slot: cleanStr(el.getAttribute('data-slot'), 32),
        variant: cleanStr(el.getAttribute('data-variant'), 32),
        href: href?.path,
        external_host: href?.host,
        region: regionName(regionEl),
        dialog: masked ? null : dialogTitle(el.closest(DIALOGS)),
        column: masked ? null : columnHeader(raw),
        disabled: disabled || isDisabled(el),
        modifier,
      },
    },
    timer: null,
    observer: null,
  }

  if (href?.external || link?.target === '_blank' || link?.hasAttribute('download') || (href && modifier)) {
    pending.push(watch)
    finalize(watch, 'external')

    return
  }

  watchEffect(watch, regionEl)

  // A same-origin link that Inertia did not take over is followed by the browser: a full load or a file download.
  if (href?.path) {
    window.setTimeout(
      safe(() => {
        if (!event.defaultPrevented) {
          finalize(watch, 'navigation')
        }
      }),
      0
    )
  }
}

function isTextField(el) {
  return (
    !!el &&
    (el.tagName === 'TEXTAREA' ||
      el.isContentEditable === true ||
      (el.tagName === 'INPUT' && !NON_TEXT_INPUT_TYPES.has(el.type)))
  )
}

function isEditable(el) {
  return (
    !!el && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.tagName === 'SELECT' || el.isContentEditable)
  )
}

function keyCombo(event, target) {
  const key = event.key

  if (key === 'Escape') {
    return 'Escape'
  }

  if (key === 'Enter') {
    const submits = target?.tagName === 'INPUT' && target.form && !NON_TEXT_INPUT_TYPES.has(target.type)

    return submits && !event.metaKey && !event.ctrlKey ? 'Enter' : null
  }

  // Ctrl+Alt is AltGr on many layouts and types characters, so it is never recorded.
  if (!(event.metaKey || event.ctrlKey) || (event.ctrlKey && event.altKey && !event.metaKey)) {
    return null
  }

  if (typeof key !== 'string' || key === '' || MODIFIER_KEYS.has(key)) {
    return null
  }

  const name = key.length === 1 ? key.toUpperCase() : key

  if (key.length === 1 && (!SHORTCUT_CHAR.test(name) || (isEditable(target) && EDITING_KEYS.has(name)))) {
    return null
  }

  return [event.metaKey ? 'Meta' : 'Ctrl', event.shiftKey ? 'Shift' : null, event.altKey ? 'Alt' : null, name]
    .filter(Boolean)
    .join('+')
}

function targetRole(el) {
  if (!el || el === document.body || el === document.documentElement) {
    return 'body'
  }

  if (el.tagName === 'INPUT' || el.tagName === 'SELECT') {
    return 'input'
  }

  if (el.tagName === 'TEXTAREA') {
    return 'textarea'
  }

  if (el.tagName === 'BUTTON' || el.getAttribute('role') === 'button') {
    return 'button'
  }

  return el.matches(DIALOGS) ? 'dialog' : 'other'
}

function onKeydown(event) {
  if (!isActive() || event.repeat || !event.isTrusted) {
    return
  }

  const target = elementOf(event.target)
  const combo = keyCombo(event, target)

  if (!combo) {
    return
  }

  const now = Date.now()

  state.lastKey = { key: combo, at: now }

  const recorded = record('ui.key', {
    target: combo,
    props: { target_role: targetRole(target), in_dialog: !!target?.closest(DIALOGS) },
  })

  if (recorded) {
    state.lastTrigger = { seq: recorded.seq, label: recorded.target ?? null, at: now, form: target?.form ?? null }
  }
}

function onPointerDown(event) {
  if (isActive()) {
    state.lastPointer = { at: Date.now(), target: elementOf(event.target) }
  }
}

export function settleClicks(effect = null) {
  ;[...pending].forEach((watch) => finalize(watch, effect))
  finishBurst()
}

export function installInteractions() {
  const options = { capture: true, passive: true }

  document.addEventListener('click', safe(onClick), options)
  document.addEventListener('keydown', safe(onKeydown), options)
  document.addEventListener('pointerdown', safe(onPointerDown), options)
}
